import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { jsonrepair } from 'jsonrepair';
import Table from '../../Table';
import { Column } from '../../Table/types';
import { ToolRendererArgs } from '../types';
import { componentToken } from '../../../style/utils';

const FADE_WIDTH = 120;

const ScrollFadeWrapper = styled.div<{
  $canScrollLeft: boolean;
  $canScrollRight: boolean;
}>`
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: ${FADE_WIDTH}px;
    pointer-events: none;
    z-index: 3;
    transition: opacity 0.2s ease;
  }

  &::before {
    left: 0;
    background: linear-gradient(
      to right,
      ${componentToken('aiChat.tableToolFade')},
      transparent
    );
    opacity: ${({ $canScrollLeft }) => ($canScrollLeft ? 1 : 0)};
  }

  &::after {
    right: 0;
    background: linear-gradient(
      to left,
      ${componentToken('aiChat.tableToolFade')},
      transparent
    );
    opacity: ${({ $canScrollRight }) => ($canScrollRight ? 1 : 0)};
  }
`;

type ChatTableProps<T extends Record<string, any>> = {
  rows: T[];
  columns: Column<T>[];
};

export const ChatTable = <T extends Record<string, any>>({
  rows,
  columns,
}: ChatTableProps<T>) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback((el: Element) => {
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    const findScrollable = (): Element | null => {
      for (const el of wrapper.querySelectorAll('div')) {
        if (el.scrollWidth <= el.clientWidth) continue;
        const { overflow, overflowX } = getComputedStyle(el);
        if (overflow === 'auto' || overflowX === 'auto') return el;
      }
      return null;
    };

    let scrollable: Element | null = null;
    const observer = new ResizeObserver(() => {
      scrollable = findScrollable();
      if (scrollable) updateScrollState(scrollable);
    });
    observer.observe(wrapper);

    scrollable = findScrollable();
    if (scrollable) updateScrollState(scrollable);

    const handleScroll = (e: Event) => updateScrollState(e.target as Element);
    wrapper.addEventListener('scroll', handleScroll, true);

    return () => {
      wrapper.removeEventListener('scroll', handleScroll, true);
      observer.disconnect();
    };
  }, [updateScrollState]);

  return (
    <ScrollFadeWrapper
      ref={wrapperRef}
      $canScrollLeft={canScrollLeft}
      $canScrollRight={canScrollRight}
    >
      <Table
        data={rows}
        columns={columns}
        wrapperSx={{
          mt: 1,
        }}
      />
    </ScrollFadeWrapper>
  );
};

// Defensively normalize tool args. Some LLM responses pass `rows` or `columns`
// as a JSON-encoded string instead of an actual array (sometimes with malformed
// JSON inside, e.g. unquoted tokens like `4-5`). We accept arrays as-is, then
// try strict JSON.parse, and finally fall back to jsonrepair which handles
// common LLM mistakes (unquoted values, trailing commas, single quotes, etc).
const normalizeArrayArg = <T,>(value: unknown): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (typeof value !== 'string') return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed as T[];
  } catch {
    // fall through to repair attempt
  }

  try {
    const repaired = JSON.parse(jsonrepair(value));
    if (Array.isArray(repaired)) return repaired as T[];
  } catch {
    // fall through to empty
  }

  return [];
};

const TableToolRenderer = (toolArgs: ToolRendererArgs) => {
  const rows = normalizeArrayArg<Record<string, any>>(toolArgs?.rows);
  const columnNames = normalizeArrayArg<string>(toolArgs?.columns);

  const columns: Column<Record<string, any>>[] = columnNames.map((c) => ({
    id: c,
    header: c,
    filterable: false,
  }));

  return <ChatTable rows={rows} columns={columns} />;
};

const tableToolRenderer = (toolArgs: ToolRendererArgs) => (
  <TableToolRenderer {...toolArgs} />
);

export default tableToolRenderer;
